import { Component, createElement } from 'react';
import { PaymentCard } from '../../utils';

export interface PaymentCardNumberProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    onChange: (e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => void;
}

export default function paymentCardNumberFactory(
    inputElement: React.ComponentType<unknown & React.InputHTMLAttributes<HTMLInputElement>>
) {
    return class PaymentCardNumber extends Component<PaymentCardNumberProps> {
        restrictCardNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            const digit = String.fromCharCode(e.which);
            if (!/^\d+$/.test(digit)) {
                return;
            }

            if (PaymentCard.hasTextSelected(target)) {
                return;
            }

            const value = (target.value + digit).replace(/\D/g, '');
            const card = PaymentCard.cardFromNumber(value);
            if ((card && value.length > card.length[card.length.length - 1]) || (!card && value.length > 16)) {
                e.preventDefault();
            }
        };

        formatCardNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            const digit = String.fromCharCode(e.which);
            if (!/^\d+$/.test(digit)) {
                return;
            }

            const { value } = target;
            const card = PaymentCard.cardFromNumber(value + digit);
            const { length } = value.replace(/\D/g, '') + digit;
            let upperLength = 16;
            if (card) {
                upperLength = card.length[card.length.length - 1];
            }
            if (length >= upperLength) {
                return;
            }

            // return if focus isn't at the end of the text
            if (target.selectionStart !== null && target.selectionStart !== value.length) {
                return;
            }

            let re;
            if (card && card.type === 'amex') {
                // AMEX cards are formatted differently
                re = /^(\d{4}|\d{4}\s\d{6})$/;
            } else {
                re = /(?:^|\s)(\d{4})$/;
            }

            // if '4242' + 4
            if (re.test(value)) {
                e.preventDefault();
                target.value = value + ' ' + digit;

                // if '424' + 2
            } else if (re.test(value + digit)) {
                e.preventDefault();
                target.value = value + digit + ' ';
            }
        };

        formatBackCardNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            const { value } = target;
            if (e.which !== 8) {
                return;
            }
            if (target.selectionStart !== null && target.selectionStart !== value.length) {
                return;
            }

            if (/\d\s$/.test(value)) {
                e.preventDefault();
                target.value = value.replace(/\d\s$/, '');
            } else if (/\s\d?$/.test(value)) {
                e.preventDefault();
                target.value = value.replace(/\d$/, '');
            }
        };

        reFormatCardNumber = (
            e:
                | React.ClipboardEvent<HTMLInputElement>
                | React.ChangeEvent<HTMLInputElement>
                | React.FormEvent<HTMLInputElement>
        ) => {
            const target = e.target as HTMLInputElement;
            target.value = PaymentCard.formatCardNumber(target.value) || '';
        };

        onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            this.formatBackCardNumber(e);
            if (this.props.onKeyDown) {
                this.props.onKeyDown(e);
            }
            if (e.defaultPrevented && this.props.onChange) {
                this.props.onChange(e);
            }
        };

        onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
            PaymentCard.restrictNumeric(e);
            this.restrictCardNumber(e);
            this.formatCardNumber(e);
            if (this.props.onKeyPress) {
                this.props.onKeyPress(e);
            }
            if (e.defaultPrevented && this.props.onChange) {
                this.props.onChange(e);
            }
        };

        onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.persist();
            window.setTimeout(() => {
                this.reFormatCardNumber(e);
                if (this.props.onPaste) {
                    this.props.onPaste(e);
                }
            }, 0);
        };

        onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.persist();
            window.setTimeout(() => {
                this.reFormatCardNumber(e);
                if (this.props.onChange) {
                    this.props.onChange(e);
                }
            }, 0);
        };

        onInput = (e: React.FormEvent<HTMLInputElement>) => {
            e.persist();
            window.setTimeout(() => {
                this.reFormatCardNumber(e);
                if (this.props.onInput) {
                    this.props.onInput(e);
                }
            }, 0);
        };

        render() {
            const Input = createElement(inputElement, {
                ...this.props,
                onKeyDown: this.onKeyDown,
                onKeyPress: this.onKeyPress,
                onPaste: this.onPaste,
                onChange: this.onChange,
                onInput: this.onInput,
                type: 'tel',
            });
            return Input;
        }
    };
}
